import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
    FormControlLabel,
    Switch,
    TablePagination,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import cuentaCorrienteProveedoresService, { CuentaCorrienteProveedorSaldo } from '../../services/cuentaCorrienteProveedoresService';
import proveedorService from '../../services/proveedorService';
import { formatCurrency } from '../../utils/formatters';

interface ProveedorSaldoSearchProps {
    open: boolean;
    onClose: () => void;
    onSelect: (proveedor: CuentaCorrienteProveedorSaldo) => void;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const DEFAULT_PAGE_SIZE = 20;

const ProveedorSaldoSearch: React.FC<ProveedorSaldoSearchProps> = ({ open, onClose, onSelect }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchTerm, setSearchTerm] = useState('');
    const [soloConSaldo, setSoloConSaldo] = useState(true);
    const [proveedores, setProveedores] = useState<CuentaCorrienteProveedorSaldo[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
    const [totalCount, setTotalCount] = useState(0);
    const saldosCacheRef = useRef<Map<number, CuentaCorrienteProveedorSaldo>>(new Map());

    const handleSearch = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const trimmed = searchTerm.trim();
            const compact = trimmed.replace(/\s+/g, '');
            const searchParam = compact !== '' ? trimmed : undefined;

            if (soloConSaldo) {
                // Solo proveedores con saldo - paginado
                const response = await cuentaCorrienteProveedoresService.getSaldos({
                    pageNumber: page + 1,
                    pageSize: rowsPerPage,
                    searchTerm: searchParam
                });
                const items = response.items || [];
                setProveedores(items);
                const count = response.totalCount ?? items.length;
                setTotalCount(count);
                if (count === 0) setError('No se encontraron proveedores.');
            } else {
                // Proveedores con y sin saldo - paginado
                const [saldosResponse, proveedoresResponse] = await Promise.all([
                    cuentaCorrienteProveedoresService.getSaldos({
                        pageNumber: 1,
                        pageSize: 10000,
                        searchTerm: searchParam
                    }),
                    proveedorService.getProveedores({
                        searchTerm: searchParam,
                        pageNumber: page + 1,
                        pageSize: rowsPerPage
                    })
                ]);

                const saldosMap = new Map(
                    (saldosResponse.items || []).map((p) => [p.nroProveedor, p])
                );
                saldosCacheRef.current = saldosMap;

                const result: CuentaCorrienteProveedorSaldo[] = (proveedoresResponse.items || []).map(
                    (p) =>
                        saldosMap.get(p.nroProveedor) ?? {
                            nroProveedor: p.nroProveedor,
                            nombre: p.nombre || '',
                            saldoTotal: 0,
                            saldoVencido: 0,
                            saldoVigente: 0,
                            ultimaActualizacion: ''
                        }
                );

                setProveedores(result);
                const count = proveedoresResponse.totalCount ?? 0;
                setTotalCount(count);
                if (count === 0) setError('No se encontraron proveedores.');
            }
        } catch (err: any) {
            setError(`Error al buscar proveedores: ${err.message || 'Error desconocido'}`);
            setProveedores([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [searchTerm, soloConSaldo, page, rowsPerPage]);

    const handleSelect = (proveedor: CuentaCorrienteProveedorSaldo) => {
        onSelect(proveedor);
        onClose();
    };

    const handleClose = () => {
        setSearchTerm('');
        setProveedores([]);
        setError(null);
        setPage(0);
        setTotalCount(0);
        onClose();
    };

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10) || DEFAULT_PAGE_SIZE);
        setPage(0);
    };

    const handleSwitchChange = (checked: boolean) => {
        setSoloConSaldo(checked);
        setPage(0);
    };

    // Reset página cuando cambia búsqueda o switch
    useEffect(() => {
        if (open) setPage(0);
    }, [open, searchTerm, soloConSaldo]);

    // Búsqueda cuando cambian criterios
    useEffect(() => {
        if (!open) {
            setSearchTerm('');
            setProveedores([]);
            setError(null);
            setPage(0);
            setTotalCount(0);
            return;
        }
        const handle = setTimeout(() => {
            handleSearch();
        }, 400);
        return () => clearTimeout(handle);
    }, [open, searchTerm, soloConSaldo, page, rowsPerPage, handleSearch]);

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallScreen}
            sx={{
                '& .MuiDialog-paper': {
                    margin: { xs: 0, sm: 2 },
                    maxHeight: { xs: '100%', sm: '90vh' },
                    height: { xs: '100%', sm: 'auto' }
                }
            }}
        >
            <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Buscar Proveedor
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Número de proveedor o nombre"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleSearch} disabled={loading} edge="end">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{ mb: 1.5 }}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={soloConSaldo}
                                onChange={(e) => handleSwitchChange(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Mostrar solo con saldo"
                    />
                </Box>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {error && !loading && (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                )}

                {!loading && proveedores.length > 0 && (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nro. Proveedor</TableCell>
                                    <TableCell>Nombre</TableCell>
                                    <TableCell align="right">Saldo Total</TableCell>
                                    <TableCell align="right">Saldo Vigente</TableCell>
                                    <TableCell align="right">Saldo Vencido</TableCell>
                                    <TableCell>Acción</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {proveedores.map((proveedor) => (
                                    <TableRow key={proveedor.nroProveedor} hover>
                                        <TableCell>{proveedor.nroProveedor}</TableCell>
                                        <TableCell>{proveedor.nombre}</TableCell>
                                        <TableCell align="right">{formatCurrency(proveedor.saldoTotal)}</TableCell>
                                        <TableCell align="right">{formatCurrency(proveedor.saldoVigente)}</TableCell>
                                        <TableCell align="right">{formatCurrency(proveedor.saldoVencido)}</TableCell>
                                        <TableCell>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => handleSelect(proveedor)}
                                            >
                                                Seleccionar
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 2 },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1
                }}
            >
                <Box sx={{ flexGrow: 1 }}>
                    {totalCount > 0 && (
                        <TablePagination
                            component="div"
                            count={totalCount}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                            labelRowsPerPage="Filas por página"
                            labelDisplayedRows={({ from, to, count }) =>
                                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                            }
                        />
                    )}
                </Box>
                <Button onClick={handleClose} variant="outlined" size="small">
                    CANCELAR
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProveedorSaldoSearch;




